/** 柜档案 API */

import request from './request';

/** 订单信息（用于柜档案页面展示） */
export interface OrderItem {
  id: string;
  orderNo: string;
  buyerName: string;
  productName: string;
  category: string;
  route: string;
  /** 航线 ID（routes.id），用于地图看板查询规划线路坐标 */
  routeId?: string;
  status: string;
  displayStatus: string;
  currentNodeCode: string;
  createdAt: string;
  updatedAt: string;
  /** 关联的柜号列表 */
  containerNos?: string[];
  /** 订单冻结的节点链（来自 node_chain_snapshot，有序 node_code 数组）；空表示无快照，前端回退全局节点 */
  nodeCodeChain?: string[];
}

/** 柜档案信息 */
export interface ContainerArchiveItem {
  id: string;
  containerNo: string;
  archiveNo: string;
  orderId: string;
  type: string;
  sealNo: string;
  status: string;
  iotDeviceId: string;
  /** 最新 IoT 温度读数（℃），listByOrderId 聚合回填；未绑定/无读数为 null */
  temperature?: number | null;
  humidity: number;
  bookingNo: string;
  vesselName: string;
  voyageNo: string;
  category: string;
  route: string;
  estimatedArrival: string;
  actualArrival: string;
  remainingStay: string;
  createdAt: string;
  updatedAt: string;
  /** 当前所在履约节点代码（如 load / ship），柜档案自身可能不带，故可选 */
  currentNodeCode?: string;
}

export interface NodeDocRuleItem {
  id: string;
  docType: string;
  substepCode?: string;
  required: boolean;
  allowMultiple: boolean;
  buyerAccessible?: boolean;
  /** DOCUMENT=正式单证；NODE_MEDIA=节点照片/视频。 */
  displayGroup?: 'DOCUMENT' | 'NODE_MEDIA';
  /** 兼容字段，由展示分组自动推导。 */
  reuseScope?: 'NODE' | 'ORDER';
  sortOrder: number;
  /** 多检查点配置（③，jsonb 文本或已解析数组）：NULL/空=无检查点；元素 {code,label,min_count} */
  checkpoints?: string | CheckpointItem[];
}

/** 多检查点单元（与 node_doc_rules.checkpoints 元素结构对应） */
export interface CheckpointItem {
  code: string;
  label: string;
  required?: boolean;
  min_count: number;
}

/** 节点记录（从数据库读取） */
export interface NodeRecordItem {
  id: string;
  containerId: string;
  nodeCode: string;
  nodeSeq: number;
  status: string;
  operatorId: string;
  operatorName: string;
  occurredAt: string;
  photos: string;
  notes: string;
  createdAt: string;
}

/** 节点配置（从 nodes 表读取） */
export interface NodeItem {
  id: string;
  nodeCode: string;
  nodeName: string;
  nodeSeq: number;
  description: string;
  isActive: boolean;
}

/** 文件附件信息 */
export interface FileAttachment {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  storageKey: string;
  bucket: string;
  sha256: string;
  uploadedBy: string;
  bizId2?: string;
  bizId3?: string;
  createdAt: string;
}

/** 查询所有柜档案（分页） */
export function listContainers(params?: { page?: number; pageSize?: number }) {
  return request<{ records: ContainerArchiveItem[]; total: number }>({
    url: '/container/list',
    method: 'GET',
    params: { page: 1, pageSize: 100, ...params },
  });
}

/** 根据订单 ID 查询柜档案（endTime：UTC ISO，已完成订单温度收口到该时刻，缺省取活体最新） */
export function listContainersByOrderId(orderId: string, endTime?: string) {
  return request<ContainerArchiveItem[]>({
    url: `/container/listByOrderId/${orderId}`,
    method: 'GET',
    params: endTime ? { endTime } : undefined,
  });
}

/** 查询柜档案详情 */
export function getContainer(id: string) {
  return request<ContainerArchiveItem>({ url: `/container/${id}`, method: 'GET' });
}

/** 新建柜档案 */
export function createContainer(data: Partial<ContainerArchiveItem>) {
  return request<void>({ url: '/container', method: 'POST', data });
}

/** 更新柜档案 */
export function updateContainer(data: Partial<ContainerArchiveItem>) {
  return request<void>({ url: '/container', method: 'PUT', data });
}

/** 推进节点 */
export function advanceNode(id: string) {
  return request<void>({ url: `/container/${id}/advance`, method: 'PUT' });
}

/** 回退节点 */
export function revertNode(id: string) {
  return request<void>({ url: `/container/${id}/revert`, method: 'PUT' });
}

/** 获取节点要求的附件规则（⑦ 传 orderId 时按订单品类过滤 apply_category） */
export function getNodeDocRules(nodeCode: string, orderId?: string) {
  return request<NodeDocRuleItem[]>({
    url: `/container/nodeRules/${nodeCode}`,
    method: 'GET',
    params: orderId ? { orderId } : undefined,
  });
}

/** 获取节点流转记录（从数据库） */
export function getNodeRecords(orderId: string) {
  return request<NodeRecordItem[]>({ url: `/container/node/list`, method: 'GET', params: { containerId: orderId } });
}

/** 获取单个订单 */
export function getOrder(orderId: string) {
  return request<OrderItem>({ url: `/order/${orderId}`, method: 'GET' });
}

/** 获取节点单据（附件列表） */
export function getNodeDocuments(nodeRecordId: string) {
  return request<NodeDocumentItem[]>({ url: `/container/node/documents`, method: 'GET', params: { nodeRecordId } });
}

/** 管理端查询订单全部附件版本（含原节点、展示分组和文件元数据）。 */
export function getOrderDocuments(orderId: string) {
  return request<NodeDocumentItem[]>({
    url: '/container/node/documents/order', method: 'GET', params: { orderId },
  });
}

/**
 * 「更新节点」弹窗即时归档：上传成功后立刻把文件挂到当前节点（幂等）。
 *
 * <p>2026-09-14：此前弹窗上传只落 file_attachments，归档只在点「提交」推进时发生，
 * 未提交就关闭 → 附件成为孤立附件，节点单据记录 / 流转日志 / 买家端与分享页
 * （只读 node_documents）均不可见。现与子步骤「处理」弹窗统一为「即上传即归档」。
 *
 * @param payload substepId 可空（主节点级单据的规则 substep_code 为 NULL，后端会按同类型规则反推）
 * @returns 归档行（含 docId，供撤销上传时同步删除）
 */
export function archiveNodeDocument(payload: {
  orderId: string;
  nodeCode: string;
  docType: string;
  fileId: string;
  substepId?: string;
  checkpointCode?: string;
  docNo?: string;
}) {
  return request<NodeDocumentItem>({
    url: '/container/node/documents/archive', method: 'POST', data: payload,
  });
}

/** 将补传/替换附件挂回已经完成的原历史节点。 */
export function supplementOrderDocument(payload: {
  orderId: string;
  nodeCode: string;
  substepId?: string;
  checkpointCode?: string;
  docType: string;
  fileId: string;
}) {
  return request<NodeDocumentItem>({
    url: '/container/node/documents/supplement', method: 'POST', data: payload,
  });
}

/**
 * 跨节点单据继承查询（决策⑤）。
 * 返回该订单下「子步骤已传主节点 + 跨节点同源 (order_id, doc_type)」的全部只读单据。
 * @param orderId 订单 ID
 * @param docType 可选单据类型过滤
 */
export function getCrossNodeDocuments(orderId: string, docType?: string) {
  const params: Record<string, string> = { orderId };
  if (docType && docType.trim()) {
    params.docType = docType.trim();
  }
  return request<NodeDocumentItem[]>({ url: `/container/substeps/documents`, method: 'GET', params });
}

/**
 * 上传子步骤单据（决策⑤ / ③ 多检查点）。先经 uploadFile 取得 fileId，
 * 再调用本接口落 node_documents（带可选 checkpointCode）。
 */
export function uploadSubstepDocument(payload: {
  orderId: string;
  nodeCode: string;
  substepCode: string;
  docType: string;
  fileId: string;
  checkpointCode?: string;
  docNo?: string;
}) {
  return request<NodeDocumentItem>({ url: `/container/substeps/documents`, method: 'POST', data: payload });
}

/** 删除子步骤单据（③ 多检查点照片移除，按 node_documents.id） */
export function deleteSubstepDocument(id: string) {
  return request<boolean>({ url: `/container/substeps/documents/${id}`, method: 'DELETE' });
}

export interface NodeDocumentItem {
  id: string;
  nodeRecordId: string;
  docType: string;
  fileId: string;
  fileName?: string;
  fileUrl?: string;
  /** 产生该单据的子步骤 code（决策⑤；非空 = 子步骤已传主节点，前端不再要求重复传） */
  substepId?: string;
  /** 冗余履约单 ID（决策⑤；跨节点同源查询用） */
  orderId?: string;
  /** 多检查点单据归属（③）：该照片所属检查点 code */
  checkpointCode?: string;
  /** 继承自上一轮单据 ID（非空 = 驳回重做后继承旧行，前端显示「继承自上一轮」标签） */
  sourceDocumentId?: string;
  createdAt: string;
  nodeCode?: string;
  nodeName?: string;
  displayGroup?: 'DOCUMENT' | 'NODE_MEDIA';
  allowMultiple?: boolean;
  fileType?: string;
  fileSize?: number;
}

/** 获取所有节点配置（从 nodes 表） */
export function getNodes() {
  return request<NodeItem[]>({ url: `/container/nodes`, method: 'GET' });
}

/** 查询订单列表（用于柜档案页面） */
export function listOrders(params?: { page?: number; pageSize?: number }) {
  return request<{ records: OrderItem[]; total: number }>({
    url: '/order/list',
    method: 'GET',
    params: { page: 1, pageSize: 100, ...params },
  });
}

/** 单据上传对（(docType, fileId)，与后端 DocFilePair 对齐） */
export interface DocFilePair {
  docType: string;
  fileId: string;
  substepCode?: string;
  checkpointCode?: string;
}

/** 推进订单节点（P0-3 契约变更：扁平 fileIds 串 → (docType,fileId) 对列表） */
export function advanceOrderNode(orderId: string, files: DocFilePair[] = []) {
  return request<boolean>({ url: `/order/${orderId}/advance`, method: 'PUT', data: files });
}

/**
 * 观察节点确认完成等需要携带节点级幂等 / 实际发生时间的推进（对象形态请求体）。
 *
 * <p>后端 {@code PUT /order/{id}/advance} 双形态兼容：裸数组（历史契约）走
 * {@link advanceOrderNode}，本函数发送对象 {@code {files, expectedFromNodeCode, actualOccurredAt, observeConfirm}}。
 *
 * <p>响应可能是：{@code true}（成功）、
 * {@code {code:'IDEMPOTENT', message, currentNodeCode}}（幂等成功，走 info Snackbar）。
 * 建柜已改由 load 节点 needArchiveCreate 入口（onRequestNewArchive）驱动，不再经 NEED_CONTAINER 软阻断。
 */
export interface AdvanceNodeMeta {
  /** 上传单证（可空） */
  files?: DocFilePair[];
  /** 期望起点节点 code（节点级幂等 L1，打开确认弹窗时快照 permission.currentNodeCode） */
  expectedFromNodeCode?: string | null;
  /** 实际发生时间（观察节点必填，后端契约格式 yyyy-MM-dd HH:mm:ss） */
  actualOccurredAt?: string | null;
  /** 是否为观察节点确认（true → 后端触发来源记为 MANUAL_OBSERVE） */
  observeConfirm?: boolean;
}

export type AdvanceNodeResult = boolean | { code?: string; message?: string; currentNodeCode?: string } | null;

export function advanceOrderNodeWithMeta(orderId: string, meta: AdvanceNodeMeta = {}) {
  return request<AdvanceNodeResult>({
    url: `/order/${orderId}/advance`,
    method: 'PUT',
    data: {
      files: meta.files ?? [],
      expectedFromNodeCode: meta.expectedFromNodeCode ?? null,
      actualOccurredAt: meta.actualOccurredAt ?? null,
      observeConfirm: meta.observeConfirm ?? false,
    },
  });
}

/**
 * 回退订单节点。
 * @param orderId 订单 ID
 * @param reason  回退原因（非必填，决策③；写入 node_transition_log.revert_reason）
 */
export function revertOrderNode(orderId: string, reason?: string) {
  const params: Record<string, string> = {};
  if (reason && reason.trim()) {
    params.reason = reason.trim();
  }
  return request<boolean>({ url: `/order/${orderId}/revert`, method: 'PUT', params });
}

/**
 * 节点可办性只读响应体（对齐后端 NodeActionPermissionVO）。
 * 用于前端角色感知：责任人（OWNER/CANDIDATE）可提交本节点，非责任人按钮置灰只读。
 * 与后端 PermissionGuard 共用同一份 node_role_bindings 口径，作为单一事实来源。
 */
export interface NodeActionPermissionVO {
  /** 当前角色是否可以提交（推进）本节点：OWNER/CANDIDATE 为 true；无权限为 false */
  canAdvance: boolean;
  /** 本节点 OWNER 角色编码集合 */
  ownerRoleKeys: string[];
  /** 本节点 OWNER 角色名称集合（用于前端文案，如【产地运营】） */
  ownerRoleNames: string[];
  /** 当前节点 code（如 produce） */
  currentNodeCode: string;
  /** 当前节点名称（如 备货生产） */
  currentNodeName: string;
  /** 下一节点 code（末节点为 null） */
  nextNodeCode: string | null;
  /** 下一节点名称（末节点为 null） */
  nextNodeName: string | null;
  /** 当前节点是否为观察节点（ship/arrive 等无人工任务，由物流回传自动流转） */
  observeNode: boolean;
  /** 是否为最后一个履约节点 */
  lastNode: boolean;
  /**
   * 推进模式：TASK_SUBMIT（常规「提交」contained） / OBSERVE_CONFIRM（观察节点「确认完成」outlined）。
   * 与 canAdvance 正交——观察节点只要角色命中 OWNER/CANDIDATE 即可手动确认（DR-19）。
   */
  advanceMode: 'TASK_SUBMIT' | 'OBSERVE_CONFIRM';
  /** 推进时是否必须填写「实际发生时间」（观察节点为 true，二次确认弹窗标必填） */
  requireActualTime: boolean;
  /** 当前节点是否已接入自动流转（如 ship←ATD / arrive←ATA） */
  autoAdvanceEnabled: boolean;
  /** 自动流转来源中文标签（如「船期 ATD」）；未接入时为 null */
  autoSourceLabel: string | null;
  /** 最近一次自动同步时间；从未自动流转过时为 null */
  lastAutoSyncAt: string | null;
}

/**
 * 查询某订单当前节点的可办性（只读端点，权限 trade:task:list，不依赖 trade:task:handle）。
 * 进入节点操作弹窗时调用，拿到 canAdvance + ownerRoleNames 后做角色感知渲染。
 * @param orderId 订单 ID
 */
export function getAdvancePermission(orderId: string) {
  return request<NodeActionPermissionVO>({ url: `/order/${orderId}/advance-permission`, method: 'GET' });
}

/** 上传文件 */
export function uploadFile(file: File, bizType: string, bizId?: string, bizId2?: string, bizId3?: string) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('bizType', bizType);
  if (bizId) {
    formData.append('bizId', bizId);
  }
  if (bizId2) {
    formData.append('bizId2', bizId2);
  }
  if (bizId3) {
    formData.append('bizId3', bizId3);
  }
  return request<FileAttachment>({ url: '/file/upload', method: 'POST', data: formData });
}

/** 批量上传文件 */
export function uploadFiles(files: File[], bizType: string, bizId?: string, bizId2?: string, bizId3?: string) {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file);
  });
  formData.append('bizType', bizType);
  if (bizId) {
    formData.append('bizId', bizId);
  }
  if (bizId2) {
    formData.append('bizId2', bizId2);
  }
  if (bizId3) {
    formData.append('bizId3', bizId3);
  }
  return request<FileAttachment[]>({ url: '/file/upload/batch', method: 'POST', data: formData });
}

/**
 * 获取文件下载 URL
 *
 * <p>默认透传当前浏览器访问 host（{@code window.location.hostname}）到后端，
 * 使生成的预签名 URL 与浏览器实际访问 MinIO 的 host 一致，避免跨主机访问（局域网 IP
 * / 公网域名）导致的 SignatureDoesNotMatch。需禁用时显式传 {@code null}。
 */
export function getFileUrl(fileId: string, host: string | null = typeof window !== 'undefined' ? window.location.hostname : null) {
  return request<string>({
    url: `/file/${fileId}/url`,
    method: 'GET',
    params: host ? { host } : undefined,
  });
}

/**
 * 获取文件「在线预览」URL（预签名，不带 Content-Disposition: attachment）。
 *
 * <p>与 getFileUrl 的区别：下载 URL 附带 attachment 头，浏览器在 iframe 中加载时
 * 同样按 attachment 处理 → 触发下载而非内嵌渲染，表现为空白色预览窗。
 * 本函数返回不带该头的 URL，专供 <iframe>/<img> 内嵌预览。
 *
 * <p>下载仍应调用 getFileUrl，以保留「下载时显示原始文件名」的能力。
 */
export function getFileInlineUrl(fileId: string, host: string | null = typeof window !== 'undefined' ? window.location.hostname : null) {
  return request<string>({
    url: `/file/${fileId}/inline-url`,
    method: 'GET',
    params: host ? { host } : undefined,
  });
}

/** 删除文件 */
export function deleteFile(fileId: string) {
  return request<boolean>({ url: `/file/${fileId}`, method: 'DELETE' });
}

/**
 * 根据业务类型和 ID 查询文件列表。
 * @param bizType 业务类型（如 node_doc）
 * @param bizId   一级业务 ID（订单 ID）
 * @param bizId2  二级业务 ID（节点 code）
 * @param bizId3  三级业务 ID（单据类型 docType，决策⑤ 跨节点同源查询用）
 */
export function listFilesByBiz(bizType: string, bizId: string, bizId2?: string, bizId3?: string) {
  const params: Record<string, string> = { bizType, bizId };
  if (bizId2) {
    params.bizId2 = bizId2;
  }
  if (bizId3) {
    params.bizId3 = bizId3;
  }
  return request<FileAttachment[]>({ url: `/file/list`, method: 'GET', params });
}

// ==================== 子步骤 API ====================

/** 子步骤执行模式 */
export type NodeSubstepExecutionMode = 'parallel' | 'sequential' | 'ACTION_ENTRY';

/** 子步骤动作类型（装柜节点子步骤灵活配置，2026-08-08） */
export type NodeSubstepActionType = 'GENERIC' | 'IOT_BIND' | 'ARCHIVE_CREATE';

/**
 * 子步骤配置（对齐后端 node_substeps 表，含动作化字段）。
 *
 * actionType 取值：
 *  - GENERIC     普通表单子步骤（默认）
 *  - IOT_BIND    IoT 设备绑定子步骤（actionConfig 驱动绑定参数与权限，如 seal_iot）
 *  - ARCHIVE_CREATE 建柜档案动作入口（executionMode='ACTION_ENTRY'，绑 container_archive）
 * executionMode='ACTION_ENTRY' 的行不占 seq、不进 depends_on、不计入必填守卫。
 */
export interface NodeSubstep {
  id: string;
  nodeCode: string;
  substepCode: string;
  substepName: string;
  /** 同一节点内序号；ACTION_ENTRY 动作入口行该值为 null（不占顺序） */
  substepSeq: number | null;
  executionMode: NodeSubstepExecutionMode;
  dependsOn?: string;
  description?: string;
  formTemplateId?: string;
  /** 动作类型（GENERIC / IOT_BIND / ARCHIVE_CREATE），缺省按 GENERIC 处理 */
  actionType?: NodeSubstepActionType;
  /** 动作配置（jsonb 字符串，如 IOT_BIND 的 {iotBindType,requiredPerm,deviceField}） */
  actionConfig?: string;
  /** 适用品类（2026-08-23 品类建模）：NULL/空=通用（所有品类可见）；GINGER=大姜；GARLIC=大蒜；ONION=洋葱；CARROT=胡萝卜。配置中心展示用 */
  category?: string | null;
  isRequired: boolean;
  isActive: boolean;
}

/**
 * 节点动作入口提示（对齐后端 NodeActionHintVO）。
 * 由 GET /order/{id}/node-action-hint 产出，驱动「建柜档案」动作入口。
 */
export interface NodeActionHintVO {
  /** 当前节点 code（如 load） */
  currentNodeCode: string;
  /** 是否需要弹出「建立柜档案」入口 */
  needArchiveCreate: boolean;
  /** 建档档案模板 ID（container_archive 模板） */
  archiveFormTemplateId: string;
  /** 预填柜号（来自订舱 shipping_bookings.container_no） */
  prefillContainerNo: string;
  /** 预填订舱号 S/O（来自订舱 shipping_bookings.booking_no） */
  bookingNo: string;
}

/** 子步骤状态记录 */
export interface NodeSubstepRecord {
  id: string;
  orderId: string;
  nodeCode: string;
  substepCode: string;
  status: 'pending' | 'in_progress' | 'completed';
  data?: string;
  completedAt?: string;
  createdAt: string;
}

/** 获取节点的子步骤配置（传 orderId 时按订单品类过滤专属子步骤） */
export function getSubstepConfig(nodeCode: string, orderId?: string) {
  return request<NodeSubstep[]>({ url: `/container/substeps/config/${nodeCode}`, method: 'GET', params: orderId ? { orderId } : undefined });
}

/** 保存子步骤配置 */
export function saveSubstep(data: Partial<NodeSubstep>) {
  return request<NodeSubstep>({ url: `/container/substeps`, method: 'POST', data });
}

/** 原子保存子步骤及其单据规则，避免两个保存入口产生部分成功。 */
export function saveSubstepWithRules(substep: Partial<NodeSubstep>, rules: Array<Record<string, unknown>>) {
  return request<NodeSubstep>({
    url: `/container/substeps/config-with-rules`,
    method: 'POST',
    data: { substep, rules },
  });
}

/** 删除子步骤配置 */
export function deleteSubstep(id: string) {
  return request<boolean>({ url: `/container/substeps/${id}`, method: 'DELETE' });
}

/** 获取订单在某节点的子步骤状态 */
export function getSubstepRecords(orderId: string, nodeCode: string) {
  return request<NodeSubstepRecord[]>({ url: `/container/substeps/records`, method: 'GET', params: { orderId, nodeCode } });
}

/** 获取订单所有节点的子步骤状态 */
export function getAllSubstepRecords(orderId: string) {
  return request<Record<string, NodeSubstepRecord[]>>({ url: `/container/substeps/records/all`, method: 'GET', params: { orderId } });
}

/** 更新子步骤状态 */
export function updateSubstepRecord(id: string, data: { status?: string; data?: string }) {
  return request<boolean>({ url: `/container/substeps/records/${id}`, method: 'PUT', data });
}

/** 初始化订单的子步骤记录 */
export function initSubstepRecords(orderId: string, nodeCode: string) {
  return request<boolean>({ url: `/container/substeps/records/init`, method: 'POST', data: { orderId, nodeCode } });
}

/** 检查节点的所有子步骤是否完成 */
export function checkNodeComplete(orderId: string, nodeCode: string) {
  return request<boolean>({ url: `/container/substeps/check-complete`, method: 'GET', params: { orderId, nodeCode } });
}

/** 节点必填子步骤完成情况明细（与后端 SubstepGuard 同源口径，含表单必填字段判定） */
export interface NodeSubstepRequirementDetail {
  /** 是否可推进（无必填子步骤或必填项已全部完成） */
  completed: boolean;
  /** 必填子步骤总数（含表单配置了必填字段的子步骤） */
  requiredTotal: number;
  /** 已完成/已填全的必填子步骤数 */
  requiredCompleted: number;
  /** 未完成必填子步骤名称列表（含「（表单未填完整）」后缀） */
  pending: string[];
}

/** 节点必填子步骤完成情况明细（供处理页「子步骤进度」权威展示，避免仅看 is_required 误判无必填） */
export function checkNodeCompleteDetail(orderId: string, nodeCode: string) {
  return request<NodeSubstepRequirementDetail>({ url: `/container/substeps/check-complete/detail`, method: 'GET', params: { orderId, nodeCode } });
}

/** 节点动作入口提示（建柜档案等 ACTION_ENTRY 动作入口，2026-08-08 装柜子步骤灵活配置） */
export function getNodeActionHint(orderId: string, nodeCode: string) {
  return request<NodeActionHintVO>({ url: `/order/${orderId}/node-action-hint`, method: 'GET', params: { nodeCode } });
}

// ==================== 表单模板 API ====================

/** 表单模板 */
export interface FormTemplate {
  id: string;
  templateCode: string;
  templateName: string;
  description?: string;
  formSchema: string;
  exportTemplatePath?: string;
  category?: string;
  isActive: boolean;
}

/** 表单数据 */
export interface FormDataItem {
  id: string;
  orderId: string;
  nodeCode: string;
  substepCode?: string;
  templateId: string;
  data: string;
  status: 'draft' | 'submitted' | 'approved';
  exportedFileId?: string;
  submittedBy?: string;
  submittedAt?: string;
  createdAt: string;
}

/** 获取表单模板列表 */
export function listFormTemplates(category?: string) {
  const params: Record<string, string> = {};
  if (category) params.category = category;
  return request<FormTemplate[]>({ url: `/form/templates`, method: 'GET', params });
}

/** 获取单个表单模板 */
export function getFormTemplate(id: string) {
  return request<FormTemplate>({ url: `/form/templates/${id}`, method: 'GET' });
}

/** 根据编码获取表单模板 */
export function getFormTemplateByCode(code: string) {
  return request<FormTemplate>({ url: `/form/templates/code/${code}`, method: 'GET' });
}

/** 保存表单模板 */
export function saveFormTemplate(data: Partial<FormTemplate>) {
  return request<FormTemplate>({ url: `/form/templates`, method: 'POST', data });
}

/** 更新表单模板 */
export function updateFormTemplate(id: string, data: Partial<FormTemplate>) {
  return request<boolean>({ url: `/form/templates/${id}`, method: 'PUT', data });
}

/** 删除表单模板（被 form_data 引用时禁止删，后端校验） */
export function deleteFormTemplate(id: string) {
  return request<boolean>({ url: `/form/templates/${id}`, method: 'DELETE' });
}

/** 校验表单模板 JSON Schema（T9 编辑器保存前调用） */
export function validateFormSchema(template: Partial<FormTemplate>) {
  return request<boolean>({ url: `/form/templates/validate`, method: 'POST', data: template });
}

// ==================== 柜档案表单采集（T8，一单多柜） ====================

/** 柜档案表单条目（对齐 container_archive 模板字段） */
export interface ContainerArchiveFormItem {
  containerNo: string;
  containerType: 'REEFER_20' | 'REEFER_40' | 'DRY_20' | 'DRY_40';
  sealNo?: string;
  vesselName?: string;
  voyageNo?: string;
  eta?: string;
}

/**
 * 提交柜档案（T8：逐柜走 container_archive 表单 → 后端投影器 upsert containers，一单多柜 1:N）。
 * 后端 saveFormData 同事务写 form_data + containers。
 *
 * <p>2026-08-08 装柜子步骤灵活配置：substepCode 默认 'archive_create'（ARCHIVE_CREATE 动作入口行），
 * 不再硬编码已退役的 'pickup_empty'。调用方可显式指定（如未来新增其它建档动作）。
 *
 * @param orderId        订单 ID
 * @param containerList  柜档案表单条目列表
 * @param substepCode    子步骤 code（默认 'archive_create'）
 */
export async function submitContainerArchive(
  orderId: string,
  containerList: ContainerArchiveFormItem[],
  substepCode: string = 'archive_create',
): Promise<void> {
  const templates = await listFormTemplates();
  const tpl = (templates || []).find(t => t.templateCode === 'container_archive');
  if (!tpl) {
    throw new Error('未找到柜档案表单模板（container_archive）');
  }
  for (const container of containerList) {
    await saveFormData({
      orderId,
      nodeCode: 'load',
      substepCode,
      templateId: tpl.id,
      data: JSON.stringify(container),
      status: 'submitted',
    } as Partial<FormDataItem>);
  }
}

/** 获取订单在某节点的表单数据 */
export function listFormData(orderId: string, nodeCode?: string) {
  const params: Record<string, string> = { orderId };
  if (nodeCode) params.nodeCode = nodeCode;
  return request<FormDataItem[]>({ url: `/form/data`, method: 'GET', params });
}

/** 保存表单数据 */
export function saveFormData(formData: Partial<FormDataItem>) {
  return request<FormDataItem>({ url: `/form/data`, method: 'POST', data: formData });
}

/** 提交表单数据 */
export function submitFormData(id: string) {
  return request<boolean>({ url: `/form/data/${id}/submit`, method: 'PUT' });
}

// ==================== 站内通知（决策⑥：advance 成功后给下一节点 OWNER 发 NODE_TODO） ====================

/** 站内通知（复用 notifications 表） */
export interface NotificationItem {
  id: string;
  recipientId: string;
  recipientType?: string;
  channel?: string;
  category: string; // 如 NODE_TODO
  title: string;
  content: string;
  priority?: string;
  read: boolean;
  refType?: string;
  refId?: string;
  createdAt: string;
  updatedAt?: string;
}

/** 我的通知列表 */
export function listMyNotifications(category?: string) {
  const params: Record<string, string> = {};
  if (category && category.trim()) {
    params.category = category.trim();
  }
  return request<NotificationItem[]>({ url: `/notification/my`, method: 'GET', params });
}

/** 未读数量 */
export function getUnreadCount(category?: string) {
  const params: Record<string, string> = {};
  if (category && category.trim()) {
    params.category = category.trim();
  }
  return request<number>({ url: `/notification/unread-count`, method: 'GET', params });
}

/** 标记单条已读 */
export function markNotificationRead(id: string) {
  return request<boolean>({ url: `/notification/${id}/read`, method: 'PUT' });
}

/** 全部标记已读 */
export function markAllNotificationsRead(category?: string) {
  const params: Record<string, string> = {};
  if (category && category.trim()) {
    params.category = category.trim();
  }
  return request<number>({ url: `/notification/read-all`, method: 'PUT', params });
}

export const containerApi = {
  list: listContainers,
  listByOrderId: listContainersByOrderId,
  getById: getContainer,
  create: createContainer,
  update: updateContainer,
  advanceNode,
  revertNode,
  getNodeDocRules,
  getNodeRecords,
  getNodes,
  listOrders,
  advanceOrderNode,
  advanceOrderNodeWithMeta,
  revertOrderNode,
  getAdvancePermission,
  uploadFile,
  uploadFiles,
  getFileUrl,
  getFileInlineUrl,
  deleteFile,
  getOrder,
  getNodeDocuments,
  getOrderDocuments,
  supplementOrderDocument,
  archiveNodeDocument,
  getCrossNodeDocuments,
  uploadSubstepDocument,
  deleteSubstepDocument,
  listFilesByBiz,
  // 子步骤 API
  getSubstepConfig,
  saveSubstep,
  saveSubstepWithRules,
  deleteSubstep,
  getSubstepRecords,
  getAllSubstepRecords,
  updateSubstepRecord,
  initSubstepRecords,
  checkNodeComplete,
  checkNodeCompleteDetail,
  getNodeActionHint,
  // 表单 API
  listFormTemplates,
  getFormTemplate,
  getFormTemplateByCode,
  saveFormTemplate,
  updateFormTemplate,
  deleteFormTemplate,
  validateFormSchema,
  listFormData,
  saveFormData,
  submitContainerArchive,
  submitFormData,
  // 站内通知
  listMyNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
};
